import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, Typography, Grid, Box, InputAdornment, IconButton, Alert, CircularProgress } from '@mui/material';
import { Email, Phone, LinkedIn, Person, Work, LocationOn, Warning, CheckCircle, Error } from '@mui/icons-material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import axios from 'axios';

const BasicInfoSection = ({ 
  resumeData, 
  setResumeData, 
  isEditing, 
  t, 
  StyledTextField,
  validationErrors = {}
}) => {
  
  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography
        variant="h5"
        sx={{ 
          fontFamily: "Poppins", 
          fontWeight: 700, 
          mb: 3,
          color: '#2c3e50',
          position: 'relative',
          pl: 3,
          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 5,
            height: '70%',
            backgroundColor: '#3498db',
            borderRadius: 3
          }
        }}
      >
        {t(`resume_editor.Basic Information`)}
      </Typography>
      
      <Paper
        elevation={0}
        sx={{ 
          p: 4, 
          borderRadius: "16px",
          border: "1px solid #e0e7ff",
          backgroundColor: '#f8fafc',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(149, 157, 165, 0.1)'
          }
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Person sx={{ color: '#3498db', fontSize: 20 }} />
              {t(`resume_editor.Full Name`)}
            </Typography>
            <StyledTextField
              fullWidth
              value={resumeData.Name || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, Name: e.target.value }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your full name")}
              error={!!validationErrors.Name}
              helperText={validationErrors.Name}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Work sx={{ color: '#3498db', fontSize: 20 }} />
              {t(`resume_editor.Professional Profile`)}
            </Typography>
            <StyledTextField
              fullWidth
              value={resumeData.Profil || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, Profil: e.target.value }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your professional profile")}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Email sx={{ color: '#3498db', fontSize: 20 }} />
              Email
            </Typography>
            <StyledTextField
              fullWidth
              value={resumeData.Email || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, Email: e.target.value }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your email")}
              error={!!validationErrors.Email}
              helperText={validationErrors.Email}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Phone sx={{ color: '#3498db', fontSize: 20 }} />
              {t(`Phone`)}
            </Typography>
            <StyledTextField
              fullWidth
              value={resumeData.Phone || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, Phone: e.target.value }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your phone number")}
              error={!!validationErrors.Phone}
              helperText={validationErrors.Phone}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <LinkedIn sx={{ color: '#3498db', fontSize: 20 }} />
              LinkedIn
            </Typography>
            <StyledTextField
              fullWidth
              value={resumeData.LinkedIn || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, LinkedIn: e.target.value }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your LinkedIn profile")}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <LocationOn sx={{ color: '#3498db', fontSize: 20 }} />
              Address
            </Typography>
            <StyledTextField
              fullWidth
              value={Array.isArray(resumeData.Address) ? resumeData.Address[0] || "" : resumeData.Address || ""}
              onChange={(e) =>
                setResumeData((prev) => ({ ...prev, Address: [e.target.value] }))
              }
              disabled={!isEditing}
              placeholder={t("resume_editor.Enter your address")}
              sx={{ 
                borderRadius: "12px",
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '&:hover fieldset': {
                    borderColor: '#3498db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3498db',
                  },
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography 
              sx={{ 
                fontFamily: "Poppins", 
                mb: 1.5, 
                fontWeight: 500,
                color: '#4a5568',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Person sx={{ color: '#3498db', fontSize: 20 }} />
              Date de Naissance
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["DatePicker"]} sx={{ pt: 0 }}>
                <DatePicker
                  value={resumeData.DateNaissance ? dayjs(resumeData.DateNaissance) : null}
                  onChange={(newValue) => {
                    const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                    setResumeData((prev) => ({ ...prev, DateNaissance: dateStr }));
                  }}
                  disabled={!isEditing}
                  format="MM-DD-YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { 
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: "12px",
                          backgroundColor: '#ffffff',
                          '&:hover fieldset': {
                            borderColor: '#3498db',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3498db',
                          },
                        }
                      }
                    }
                  }}
                />
              </DemoContainer>
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default BasicInfoSection;